// Незавершённая тренировка: живёт в памяти и в IndexedDB (таблица activeSession), поэтому
// закрытая вкладка или перезагрузка телефона продолжают её с того же места.
// Сервер узнаёт о ней через очередь мутаций (PUT тренировки и подходов с UUID клиента).

import { useQuery } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'

import { captureIdentity, getActiveUserId, subscribeIdentity } from '@/api/identity'
import { api } from '@/api/client'
import { toIsoDate } from '@/lib/periods'
import type { SessionPage, Tracking } from '@/pages/workouts/api'

import { getOfflineDb } from './db'
import { pendingMutations } from './queue'

export type ActiveSet = {
  id: string
  set_index: number
  weight_kg: number | null
  reps: number | null
  duration_s: number | null
  distance_m: number | null
  is_warmup: boolean
  completed: boolean
  completed_at: string | null
  rest_s: number | null
  /** Подход уже поставлен в очередь на сервер: его правки и удаление тоже уходят туда. */
  saved: boolean
}

export type ActiveExercise = {
  /** Ключ строки упражнения в тренировке (одно упражнение можно добавить дважды). */
  key: string
  exercise_id: string
  name: string
  tracking: Tracking
  /** exercise_position на сервере — не меняется после добавления. */
  position: number
  /** Отдых из шаблона, секунд. */
  rest_s: number | null
  /** «3 × 8 × 80 кг» — как было в прошлый раз. */
  last_summary: string | null
  /** Подпись прогрессии из плана: «+2,5 кг: прошлый раз всё выполнено». */
  reason: string | null
  warning: string | null
  /** Значения для первого подхода, если подходов не осталось (план или прошлый раз). */
  seed: Pick<ActiveSet, 'weight_kg' | 'reps' | 'duration_s' | 'distance_m' | 'rest_s'> | null
  sets: ActiveSet[]
}

export type ActiveWorkout = {
  id: string
  /** У неначатой (`draft`) — когда подготовлена; «Старт» ставит настоящее время начала. */
  started_at: string
  /**
   * Подготовлена, но не начата: упражнения и подходы можно смотреть и править, на сервер
   * ничего не уходит, секундомер стоит. Нет поля — тренировка идёт (так и в старых записях).
   */
  draft?: boolean
  template_id: string | null
  template_name: string | null
  planned_session_id: string | null
  notes: string | null
  exercises: ActiveExercise[]
  /** На паузе с этого момента; нет — тренировка идёт. */
  paused_at?: string | null
  /** Сколько секунд уже простояла на паузе (без текущей). */
  paused_s?: number
}

/** Краткие сведения о незавершённой тренировке — для других экранов («Сегодня»). */
export type ActiveSessionInfo = {
  id: string
  started_at: string
  title: string
  sets_done: number
  /** 'local' — на этом устройстве (можно продолжить сразу), 'server' — начата на другом. */
  source: 'local' | 'server'
  /** Подготовлена на этом устройстве, но ещё не начата. */
  draft?: boolean
}

const KEY = 'current'

type Store = { workout: ActiveWorkout | null; ready: boolean }

let store: Store = { workout: null, ready: false }
const listeners = new Set<() => void>()
let loading: Promise<ActiveWorkout | null> | null = null
let writes: Promise<unknown> = Promise.resolve()

function emit(next: Store): void {
  store = next
  for (const listener of listeners) listener()
}

/** Прочитать тренировку из IndexedDB (один раз за жизнь страницы). */
export function loadActiveWorkout(): Promise<ActiveWorkout | null> {
  if (!getActiveUserId()) return Promise.resolve(null)
  if (store.ready) return Promise.resolve(store.workout)
  const identity = captureIdentity()
  const offlineDb = getOfflineDb()
  loading ??= offlineDb.activeSession
    .get(KEY)
    .then((row) => (row?.data as ActiveWorkout | undefined) ?? null)
    .catch(() => null)
    .then((workout) => {
      if (!identity.isCurrent()) return null
      // Пока читали, могли уже начать новую — она главнее.
      if (store.ready) return store.workout
      if (workout && isStaleDraft(workout)) {
        void setActiveWorkout(null)
        return null
      }
      emit({ workout, ready: true })
      return store.workout
    })
  return loading
}

/** Неначатая тренировка, подготовленная не сегодня, — забывается сама. */
export function isStaleDraft(workout: ActiveWorkout, now: Date = new Date()): boolean {
  return Boolean(workout.draft) && toIsoDate(new Date(workout.started_at)) < toIsoDate(now)
}

export function getActiveWorkout(): ActiveWorkout | null {
  return getActiveUserId() ? store.workout : null
}

/** Заменить тренировку (null — завершена или отменена) и сохранить в IndexedDB. */
export function setActiveWorkout(workout: ActiveWorkout | null): Promise<void> {
  const offlineDb = getOfflineDb()
  emit({ workout, ready: true })
  const write = writes.then(async (): Promise<void> => {
    if (workout) await offlineDb.activeSession.put({ key: KEY, data: workout, updated_at: new Date().toISOString() })
    else await offlineDb.activeSession.delete(KEY)
  })
  writes = write.catch(() => undefined)
  return write.then(() => undefined)
}

/** Изменить текущую тренировку функцией; без тренировки ничего не делает. */
export function updateActiveWorkout(change: (workout: ActiveWorkout) => ActiveWorkout): Promise<void> {
  const current = store.workout
  if (!current) return Promise.resolve()
  return setActiveWorkout(change(current))
}

/** Для тестов: забыть состояние в памяти (как после перезагрузки страницы). */
export function resetActiveWorkoutMemory(): void {
  store = { workout: null, ready: false }
  loading = null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (!store.ready) void loadActiveWorkout()
  return () => listeners.delete(listener)
}

subscribeIdentity(() => {
  loading = null
  emit({ workout: null, ready: false })
  if (getActiveUserId()) void loadActiveWorkout()
})
const getSnapshot = () => { void getActiveUserId(); return store }

/** Полная незавершённая тренировка этого устройства (экран /workout). */
export function useActiveWorkout(): Store {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function workoutTitle(workout: Pick<ActiveWorkout, 'template_name'>): string {
  return workout.template_name ?? 'Тренировка'
}

const OPEN_WINDOW_MS = 12 * 60 * 60 * 1000

/** Незавершённая тренировка на сервере (начата на другом устройстве), если на этом её нет. */
async function fetchServerOpen(): Promise<ActiveSessionInfo | null> {
  const page = await api.get<SessionPage>('/sessions', { query: { limit: 5 } })
  const finishing = new Set(
    (await pendingMutations()).filter((m) => m.url.endsWith('/finish')).map((m) => m.url.split('/')[3]),
  )
  const open = page.items.find(
    (s) => s.ended_at === null && !finishing.has(s.id) && Date.now() - new Date(s.started_at).getTime() < OPEN_WINDOW_MS,
  )
  if (!open) return null
  return {
    id: open.id,
    started_at: open.started_at,
    title: open.template_name ?? 'Тренировка',
    sets_done: open.sets_done,
    source: 'server',
  }
}

export const activeSessionKeys = { server: ['training', 'open-session'] as const }

/**
 * Есть ли незавершённая тренировка: сначала этого устройства (IndexedDB), иначе — открытая
 * на сервере. `loading` — пока не прочитана IndexedDB.
 */
export function useActiveSession(): { session: ActiveSessionInfo | null; loading: boolean } {
  const { workout, ready } = useActiveWorkout()
  const server = useQuery({
    queryKey: activeSessionKeys.server,
    queryFn: fetchServerOpen,
    enabled: Boolean(getActiveUserId()) && ready && !workout,
    staleTime: 60_000,
  })
  if (!ready) return { session: null, loading: true }
  if (workout) {
    return {
      session: {
        id: workout.id,
        started_at: workout.started_at,
        title: workoutTitle(workout),
        sets_done: workout.exercises.reduce((n, e) => n + e.sets.filter((s) => s.completed).length, 0),
        source: 'local',
        draft: Boolean(workout.draft),
      },
      loading: false,
    }
  }
  return { session: server.data ?? null, loading: false }
}
